import { useCallback, useEffect, useRef, useState } from 'react';
export function useAnchoredPopover({ isOpen, onClose, expand = false, defaultMaxHeight = 240, viewportPadding = 16, gap = 4, }) {
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);
    const [position, setPosition] = useState(undefined);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);
    const updatePosition = useCallback(() => {
        const triggerRect = triggerRef.current?.getBoundingClientRect();
        if (!triggerRect)
            return;
        const popoverRect = popoverRef.current?.getBoundingClientRect();
        const spaceBelow = window.innerHeight - triggerRect.bottom - gap - viewportPadding;
        const spaceAbove = triggerRect.top - gap - viewportPadding;
        // Flip above the trigger when the content doesn't fit below but fits
        // better above. Measure the natural content height via `scrollHeight`,
        // not `getBoundingClientRect`, because the rendered box is already
        // clamped to `maxHeight` (overflow-hidden) and would otherwise never
        // appear taller than the space below. Until measured, default to below.
        const popoverHeight = popoverRef.current?.scrollHeight ?? 0;
        const shouldFlipAbove = popoverHeight > spaceBelow && spaceAbove > spaceBelow;
        const availableHeight = shouldFlipAbove ? spaceAbove : spaceBelow;
        const maxHeight = expand
            ? Math.max(availableHeight, defaultMaxHeight)
            : Math.min(defaultMaxHeight, availableHeight);
        const top = shouldFlipAbove ? triggerRect.top - gap - Math.min(popoverHeight, maxHeight) : triggerRect.bottom + gap;
        const popoverWidth = popoverRect?.width ?? triggerRect.width;
        const maxLeft = window.innerWidth - popoverWidth - viewportPadding;
        const left = Math.max(viewportPadding, Math.min(triggerRect.left, maxLeft));
        setPosition((previousPosition) => {
            const nextPosition = { top, left, width: triggerRect.width, maxHeight };
            if (previousPosition &&
                previousPosition.top === nextPosition.top &&
                previousPosition.left === nextPosition.left &&
                previousPosition.width === nextPosition.width &&
                previousPosition.maxHeight === nextPosition.maxHeight) {
                return previousPosition;
            }
            return nextPosition;
        });
    }, [expand, defaultMaxHeight, viewportPadding, gap]);
    useEffect(() => {
        if (!isOpen) {
            setPosition(undefined);
            return;
        }
        updatePosition();
        const handleClickOutside = (event) => {
            const target = event.target;
            if (triggerRef.current?.contains(target))
                return;
            if (popoverRef.current?.contains(target))
                return;
            onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, onClose, handleKeyDown, updatePosition]);
    // The popover is only mounted once `position` is set. Observe it once it
    // exists so we re-measure with its real (unclamped) content height — this is
    // what lets the popover flip above the trigger on a small screen, not only
    // while resizing.
    const hasPosition = position !== undefined;
    useEffect(() => {
        if (!isOpen || !hasPosition || !popoverRef.current)
            return;
        const resizeObserver = new ResizeObserver(updatePosition);
        resizeObserver.observe(popoverRef.current);
        return () => resizeObserver.disconnect();
    }, [isOpen, hasPosition, updatePosition]);
    return { triggerRef, popoverRef, position };
}
